import type { ReactElement, RefObject } from "react";
import { MAX_EMAIL_CHARS, MAX_FAMILY_NAME_CHARS, MAX_FIRST_NAME_CHARS, MAX_PASSWORD_CHARS, MIN_FAMILY_NAME_CHARS, MIN_FIRST_NAME_CHARS, MIN_PASSWORD_CHARS, pixelType, type SPACING } from "odyssey-shared";
import type { EnKey } from "odyssey-lang";
import { Button, Checkbox, KIT_METRICS, TextField, TextLink, logical } from "../ui/kit/index.ts";
import { say } from "../words.ts";
import { ENTRY_METRICS, EntryScreen } from "./entry-screen.tsx";
import { makesCharacter, type LoginField, type LoginState } from "./login-state.ts";

/**
 * The login screen on the kit (P1.9-T12, ADR-061 and its amendments; the design export is a visual reference
 * only): the game's wordmark over one window holding the log in form, the register form or the name step
 * (P1.4-T18, P1.4-T26), drawn from a `LoginState` on the logical pixel grid, in the layout the entry screens
 * share (`entry-screen.tsx`). Log in and register each ask for an email and a password (ADR-064's
 * amendment "accounts have no account name"); the name step asks for the fields its naming grant needs, the
 * family name and the first name for a new account and the family name alone for an account whose characters
 * predate families (ADR-065 decision 3), or for the character name alone when the grant names no needs. A step
 * that makes a character continues to the Look step (`look-step.tsx`, ADR-094 decision 23), where the voice is
 * chosen with the look, so its button reads Continue; the family step alone makes none and enters the world. Every
 * sentence sits in the note line of a field (`LoginNote`), which the field shows only while it holds one; busy
 * shows on the submit button, which spans the form and keeps its size. The log in form's "Keep me logged in"
 * checkbox sits under the password, unticked until the player ticks it (P1.4-T7, ADR-075). The forgot-password
 * link has reserved space and no control on the log in form, and sign-in with a provider on the register form;
 * the log in form gives the provider's reserved row to the checkbox, so it still fits the phone minimum of 445
 * by 230 (P1.9-T51), until a provider is chosen and the rows are laid out again.
 *
 * The register form asks for the password twice, "Confirm password" under "Password", and refuses a mismatch
 * at the confirm field before anything is sent; the confirmation is the page's alone (P1.9-T92, ADR-061's
 * amendment "the password is typed twice to create an account, and can be shown"). Every password field carries
 * the kit's show or hide button, as the design draws it, and the register form's two share one shown state, so
 * either button shows and hides both. The confirm field keeps its note line's space while it is empty, so the
 * mismatch appearing as the field is left moves nothing under the pointer. With its third field the register
 * form is taller than the phone minimum and scrolls down inside the backdrop there, never across.
 *
 * The fields carry the limits the gateway applies, from
 * `odyssey-shared`, so the typing stops where the gateway stops accepting, and the constraints the page's
 * form carried before the kit (`required`, `minlength`, `pattern`). The form is `noValidate`, so the browser
 * shows no bubble of its own: the page checks the same rules before it sends (`fieldProblem`) and shows a
 * field that breaks one in that field's note line, in the kit's look.
 */

/**
 * The words of one form: its title, its submit button, its mode link and its busy words.
 */
export interface FormWords {
  readonly title: string;
  readonly submit: string;
  readonly switch: string;
  readonly busy: string;
}

/**
 * The name fields' `pattern` as a browser compiles it (anchored, with the `v` flag, where a hyphen in a class
 * is escaped): the characters of each part of a name, its lengths being the field's `minLength` and
 * `maxLength` (ADR-065 decision 2).
 */
export const NAME_PART_FIELD_PATTERN = "[A-Za-z0-9_\\-]+";

/**
 * A spacing step of the token table, checked at compile time.
 *
 * @param step One of `SPACING`.
 * @returns The step in logical pixels.
 */
function space(step: (typeof SPACING)[number]): number {
  return step;
}

/**
 * The screen's measurements in logical pixels: the layout the entry screens share (`ENTRY_METRICS`: the
 * wordmark, the gap under it and the backdrop's grid), and the form's own. The window is 184 wide, as the design
 * draws it (ADR-061's amendment "the kit and the entry forms match the design"), and the form's rows are the
 * design's 6 apart. The log in form's link row holds "Create an account" and, beside it, the reserved
 * forgot-password slot, which takes the rest of the row; the slot is a link's line tall and the provider slot a
 * button's height.
 */
export const LOGIN_METRICS = {
  ...ENTRY_METRICS,
  window: 184,
  formGap: space(6),
  linkGap: space(4),
  link: pixelType("label").lineHeight,
  provider: KIT_METRICS.button.height,
} as const;

/**
 * What the screen draws and whom it tells: the state; a ref to each field's input, which the page focuses
 * as the state asks; whether the server refuses logins now (offline or under maintenance, ADR-078 decision 3),
 * which disables the log in and register forms' button; and the player's actions.
 */
export interface LoginScreenProps {
  readonly state: LoginState;
  readonly inputs: Readonly<Record<LoginField, RefObject<HTMLInputElement | null>>>;
  readonly blocked?: boolean;
  readonly onType: (field: LoginField, value: string) => void;
  readonly onKeep: (keep: boolean) => void;
  readonly onLeave: (field: LoginField) => void;
  readonly onReveal: () => void;
  readonly onSubmit: () => void;
  readonly onSwitch: () => void;
  readonly onLogOutEverywhere: () => void;
}

/**
 * The keys of each form's words. The name step that makes a character has Continue on its button, since the Look
 * step follows.
 */
const FORM_KEYS = {
  login: { title: "account.login.title", submit: "account.login.submit", switch: "account.login.switch", busy: "account.login.busy" },
  register: { title: "account.register.title", submit: "account.register.submit", switch: "account.register.switch", busy: "account.register.busy" },
  naming: { title: "account.naming.title", submit: "account.naming.next", switch: "account.naming.switch", busy: "account.naming.busy" },
  family: { title: "account.family.title", submit: "account.family.submit", switch: "account.family.switch", busy: "account.family.busy" },
} as const satisfies Readonly<Record<string, Readonly<Record<keyof FormWords, EnKey>>>>;

/**
 * The words of the form on screen: the name step that asks for the family name alone is the family step, and
 * every other name step keeps the name step's words, its button Continue, since the Look step follows.
 *
 * @param state The screen's state.
 * @returns The form's words.
 */
export function formWords(state: LoginState): FormWords {
  const keys = FORM_KEYS[state.mode !== "naming" ? state.mode : makesCharacter(state) ? "naming" : "family"];
  return { title: say(keys.title), submit: say(keys.submit), switch: say(keys.switch), busy: say(keys.busy) };
}

/**
 * What a field's note line shows: the note when it sits under that field, as an error or a hint, else the
 * name step's note under its name fields, else nothing.
 *
 * @param state The screen's state.
 * @param field The field.
 * @returns The field's `hint` or `error` props.
 */
function noteOf(state: LoginState, field: LoginField): { readonly hint?: string; readonly error?: string } {
  const note = state.note;
  if (note !== null && note.field === field) {
    return note.tone === "error" ? { error: note.text } : { hint: note.text };
  }
  if (field === "characterName") {
    return { hint: say("account.namingNote") };
  }
  return field === "familyName" ? { hint: say("account.familyNote") } : {};
}

/**
 * The name step's fields: the family name and the first name its grant asks for, in the form's order, or the
 * one-field step's character name when the grant names no needs, each stopping the typing at its part's
 * limit and counting its characters, since that limit is one a player meets.
 *
 * @param props The screen's props.
 * @returns The fields.
 */
function NameFields({ state, inputs, onType }: Pick<LoginScreenProps, "state" | "inputs" | "onType">): ReactElement {
  if (state.needs === null) {
    return (
      <>
        <TextField key="characterName" label={say("account.labels.characterName")} value={state.characterName} onChange={(value) => onType("characterName", value)} name="character-name" autoComplete="off" required minLength={MIN_FIRST_NAME_CHARS} maxLength={MAX_FIRST_NAME_CHARS} counter pattern={NAME_PART_FIELD_PATTERN} inputRef={inputs.characterName} {...noteOf(state, "characterName")} />
      </>
    );
  }
  return (
    <>
      {state.needs.map((field) =>
        field === "familyName" ? (
          <TextField key="familyName" label={say("account.labels.familyName")} value={state.familyName} onChange={(value) => onType("familyName", value)} name="family-name" autoComplete="off" required minLength={MIN_FAMILY_NAME_CHARS} maxLength={MAX_FAMILY_NAME_CHARS} counter pattern={NAME_PART_FIELD_PATTERN} inputRef={inputs.familyName} {...noteOf(state, "familyName")} />
        ) : (
          <TextField key="firstName" label={say("account.labels.firstName")} value={state.characterName} onChange={(value) => onType("characterName", value)} name="first-name" autoComplete="off" required minLength={MIN_FIRST_NAME_CHARS} maxLength={MAX_FIRST_NAME_CHARS} counter pattern={NAME_PART_FIELD_PATTERN} inputRef={inputs.characterName} {...noteOf(state, "characterName")} />
        ),
      )}
    </>
  );
}

/**
 * The form's fields: the email and the password on the log in and register forms, with "Keep me logged in"
 * under them on the log in form and the password's confirmation on the register form, and the name step's
 * fields on the name step, each stopping the typing at the gateway's limit. The password and its confirmation
 * each carry the show or hide button, disabled while a request is out, and are shown and hidden together; the
 * confirmation reserves its note line and tells the page when the keyboard leaves it. Only the names count their characters, since their limits are ones a player meets; no
 * player types to an email's 254 or a password's 200.
 *
 * @param props The screen's props.
 * @returns The fields.
 */
function Fields({ state, inputs, onType, onKeep, onLeave, onReveal }: Pick<LoginScreenProps, "state" | "inputs" | "onType" | "onKeep" | "onLeave" | "onReveal">): ReactElement {
  if (state.mode === "naming") {
    return <NameFields state={state} inputs={inputs} onType={onType} />;
  }
  const loggingIn = state.mode === "login";
  const reveal = { label: state.revealed ? say("account.reveal.hide") : say("account.reveal.show"), onToggle: onReveal, disabled: state.busy };
  return (
    <>
      <TextField key="email" label={say("account.labels.email")} type="email" value={state.email} onChange={(value) => onType("email", value)} name="email" autoComplete={loggingIn ? "username" : "email"} required maxLength={MAX_EMAIL_CHARS} inputRef={inputs.email} {...noteOf(state, "email")} />
      <TextField key="password" label={say("account.labels.password")} type="password" value={state.password} onChange={(value) => onType("password", value)} name="password" autoComplete={loggingIn ? "current-password" : "new-password"} required minLength={MIN_PASSWORD_CHARS} maxLength={MAX_PASSWORD_CHARS} inputRef={inputs.password} revealed={state.revealed} reveal={reveal} {...noteOf(state, "password")} />
      {loggingIn ? null : <TextField key="confirm" label={say("account.labels.confirm")} type="password" value={state.confirm} onChange={(value) => onType("confirm", value)} name="confirm-password" autoComplete="new-password" required maxLength={MAX_PASSWORD_CHARS} inputRef={inputs.confirm} revealed={state.revealed} reveal={reveal} reserveNote onLeave={() => onLeave("confirm")} {...noteOf(state, "confirm")} />}
      {loggingIn ? (
        <div key="keep" className="ot-login__keep" data-login="keep">
          <Checkbox label={say("account.keep")} checked={state.keep} onChange={onKeep} disabled={state.busy} />
        </div>
      ) : null}
    </>
  );
}

/**
 * The row of links under the submit button: the mode link, and beside it the forgot-password slot on the log
 * in form or Log out everywhere on the name step.
 *
 * @param props The screen's props.
 * @returns The row.
 */
function Links({ state, onSwitch, onLogOutEverywhere }: Pick<LoginScreenProps, "state" | "onSwitch" | "onLogOutEverywhere">): ReactElement {
  return (
    <div className="ot-login__links" data-login="links" style={{ height: logical(LOGIN_METRICS.link), gap: logical(LOGIN_METRICS.linkGap) }}>
      <TextLink onClick={onSwitch}>{formWords(state).switch}</TextLink>
      {state.mode === "login" ? <span className="ot-login__forgot" data-reserved="forgot-password" aria-hidden="true" style={{ height: logical(LOGIN_METRICS.link) }} /> : null}
      {state.mode === "naming" ? <TextLink onClick={onLogOutEverywhere}>{say("account.everywhere")}</TextLink> : null}
    </div>
  );
}

/**
 * The login screen: the entry layout's backdrop, wordmark and window, the window titled and filled by the
 * form the state is on.
 *
 * @param props The state, the inputs' refs and the player's actions.
 * @returns The screen.
 */
export function LoginScreen({ state, inputs, blocked = false, onType, onKeep, onLeave, onReveal, onSubmit, onSwitch, onLogOutEverywhere }: LoginScreenProps): ReactElement {
  const layout = state.needs === null ? state.mode : `${state.mode}:${state.needs.join(",")}`;
  const words = formWords(state);
  return (
    <EntryScreen mode={state.mode} layout={layout} title={words.title} width={LOGIN_METRICS.window}>
      <form
        className="ot-login__form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
        style={{ gap: logical(LOGIN_METRICS.formGap) }}
      >
        <Fields state={state} inputs={inputs} onType={onType} onKeep={onKeep} onLeave={onLeave} onReveal={onReveal} />
        <div className="ot-login__actions">
          <Button variant="primary" type="submit" busy={state.busy} busyLabel={words.busy} disabled={blocked && state.mode !== "naming"} fullWidth>
            {words.submit}
          </Button>
        </div>
        <Links state={state} onSwitch={onSwitch} onLogOutEverywhere={onLogOutEverywhere} />
        {state.mode === "register" ? <div className="ot-login__provider" data-reserved="sign-in-provider" aria-hidden="true" style={{ height: logical(LOGIN_METRICS.provider) }} /> : null}
      </form>
    </EntryScreen>
  );
}
